import type { IUserModel } from '~/types/apiService';

const STORAGE_KEY = 'userAutoConfirmSettings';

export interface AutoConfirmFlags {
  agreeToCraftingRules: boolean;
  agreeToMiningRules: boolean;
  agreeToBuyPearlRules: boolean;
}

const useSharedAutoConfirmFlags = () =>
  useState<AutoConfirmFlags>('autoConfirmFlagsState', () => ({
    agreeToCraftingRules: false,
    agreeToMiningRules: false,
    agreeToBuyPearlRules: false
  }));

const useAddressLoaded = () => useState<string | null>('autoConfirmAddressLoaded', () => null);

export function useAutoConfirmFlags(addressKey?: keyof AutoConfirmFlags) {
  const { data: userData } = useNuxtData<IUserModel>('userData');
  const address = computed(() => userData.value?.user.address);

  const currentFlags = useSharedAutoConfirmFlags();
  const addressLoaded = useAddressLoaded();

  const loadAllFlags = () => {
    if (!import.meta.client) return;

    const currentAddress = address.value;

    if (addressLoaded.value === currentAddress && currentAddress !== null) {
      return;
    }

    addressLoaded.value = null;

    if (!currentAddress) {
      currentFlags.value = { agreeToCraftingRules: false, agreeToMiningRules: false, agreeToBuyPearlRules: false };
      return;
    }

    try {
      const allSettings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const userSettings: AutoConfirmFlags | undefined = allSettings[currentAddress];

      if (userSettings) {
        currentFlags.value = userSettings;
      } else {
        currentFlags.value = { agreeToCraftingRules: false, agreeToMiningRules: false, agreeToBuyPearlRules: false };
      }

      addressLoaded.value = currentAddress;
    } catch (e) {
      console.error('Error loading auto-confirm settings from localStorage:', e);
    }
  };

  const saveAllFlags = () => {
    if (!import.meta.client || !address.value) return;

    const allSettings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

    allSettings[address.value] = currentFlags.value;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(allSettings));
  };

  watch(
    address,
    (newAddress, oldAddress) => {
      if (newAddress !== oldAddress) {
        addressLoaded.value = null;
        loadAllFlags();
      }
    },
    { immediate: true }
  );

  const shouldBeHidden = computed(() => {
    if (!addressKey) return false;
    return currentFlags.value[addressKey] === true;
  });

  return {
    address,
    currentFlags,
    loadAllFlags,
    saveAllFlags,
    shouldBeHidden
  };
}
